import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableMetricaItem({ id, nome, grupo, favorita, favoritasAtivas, onToggleFavorita, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <button {...attributes} {...listeners}
        className="flex flex-col gap-0.5 cursor-grab active:cursor-grabbing flex-shrink-0"
        style={{ color: '#555', background: 'none', border: 'none', padding: 0 }}>
        <div style={{ width: '14px', height: '1.5px', backgroundColor: 'currentColor', borderRadius: '1px' }} />
        <div style={{ width: '14px', height: '1.5px', backgroundColor: 'currentColor', borderRadius: '1px' }} />
        <div style={{ width: '14px', height: '1.5px', backgroundColor: 'currentColor', borderRadius: '1px' }} />
      </button>
      <button onClick={onToggle}
        className="relative flex-shrink-0"
        style={{ width: '32px', height: '20px', borderRadius: '99px', backgroundColor: '#DCF127', border: 'none', cursor: 'pointer', position: 'relative' }}>
        <div style={{ position: 'absolute', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'white', top: '4px', left: '18px', transition: 'left 0.15s' }} />
      </button>
      <span style={{ fontSize: '13px', color: 'white', flex: 1 }}>{nome}</span>
      <span style={{ fontSize: '11px', color: '#444' }}>{grupo}</span>
      <button onClick={onToggleFavorita}
        style={{
          fontSize: '11px',
          padding: '2px 8px',
          borderRadius: '4px',
          backgroundColor: favorita ? '#2a2a00' : '#1a1a1a',
          color: favorita ? '#DCF127' : favoritasAtivas >= 3 ? '#333' : '#555',
          border: favorita ? '1px solid #4a4a00' : '1px solid #222',
          cursor: 'pointer',
        }}>★</button>
    </div>
  );
}

function AdsConfiguracoes({ setPaginaAtiva }) {
  const [metricasGlobais, setMetricasGlobais] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [metricasCliente, setMetricasCliente] = useState([]);
  const [novaMetrica, setNovaMetrica] = useState({ nome: '', tipo: 'numero', grupo: 'Resultado' });
  const [editandoMetrica, setEditandoMetrica] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const grupos = ['Investimento', 'Alcance', 'Engajamento', 'Conversão', 'Resultado'];
  const tipos = [
    { val: 'moeda', label: 'moeda (R$)' },
    { val: 'numero', label: 'número' },
    { val: 'percentual', label: 'percentual (%)' },
    { val: 'multiplicador', label: 'multiplicador (x)' },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => { carregarDados(); }, []);
  useEffect(() => { if (clienteSelecionado) carregarMetricasCliente(clienteSelecionado.id); }, [clienteSelecionado]);

  const carregarDados = async () => {
    const [{ data: metricas }, { data: clts }] = await Promise.all([
      supabase.from('ads_metricas_config').select('*').order('grupo').order('ordem'),
      supabase.from('clientes').select('id, nome').eq('modulo_ads', true).eq('ativo', true).order('nome'),
    ]);
    setMetricasGlobais(metricas || []);
    setClientes(clts || []);
    if (clts?.length) setClienteSelecionado(prev => prev ?? clts[0]);
    setCarregando(false);
  };

  const recarregarMetricasGlobais = async () => {
    const { data } = await supabase.from('ads_metricas_config').select('*').order('grupo').order('ordem');
    setMetricasGlobais(data || []);
  };

  const carregarMetricasCliente = async (clienteId) => {
    const { data } = await supabase
      .from('ads_metricas_cliente')
      .select('*, ads_metricas_config(*)')
      .eq('cliente_id', clienteId)
      .order('ordem');
    setMetricasCliente(data || []);
  };

  const toggleMetricaCliente = async (metricaId) => {
    if (!clienteSelecionado) return;
    const existente = metricasCliente.find(m => m.metrica_id === metricaId);
    if (existente) {
      await supabase.from('ads_metricas_cliente').delete().eq('id', existente.id);
    } else {
      await supabase.from('ads_metricas_cliente').insert({
        cliente_id: clienteSelecionado.id,
        metrica_id: metricaId,
        favorita: false,
        ordem: metricasCliente.length,
      });
    }
    carregarMetricasCliente(clienteSelecionado.id);
  };

  const toggleFavorita = async (metricaClienteId, atual) => {
    const favoritasAtivas = metricasCliente.filter(m => m.favorita).length;
    if (!atual && favoritasAtivas >= 3) return;
    await supabase.from('ads_metricas_cliente').update({ favorita: !atual }).eq('id', metricaClienteId);
    carregarMetricasCliente(clienteSelecionado.id);
  };

  const toggleMetricaGlobal = async (metricaId, ativo) => {
    await supabase.from('ads_metricas_config').update({ ativo: !ativo }).eq('id', metricaId);
    await recarregarMetricasGlobais();
    if (clienteSelecionado) carregarMetricasCliente(clienteSelecionado.id);
  };

  const adicionarMetrica = async () => {
    if (!novaMetrica.nome.trim()) return;
    await supabase.from('ads_metricas_config').insert({
      nome: novaMetrica.nome.trim(),
      tipo: novaMetrica.tipo,
      grupo: novaMetrica.grupo,
      ativo: true,
      ordem: metricasGlobais.length + 1,
    });
    setNovaMetrica({ nome: '', tipo: 'numero', grupo: 'Resultado' });
    await recarregarMetricasGlobais();
    if (clienteSelecionado) await carregarMetricasCliente(clienteSelecionado.id);
  };

  const salvarEdicaoMetrica = async (id) => {
    await supabase.from('ads_metricas_config').update({
      nome: editandoMetrica.nome,
      tipo: editandoMetrica.tipo,
      grupo: editandoMetrica.grupo,
    }).eq('id', id);
    setEditandoMetrica(null);
    await recarregarMetricasGlobais();
    if (clienteSelecionado) carregarMetricasCliente(clienteSelecionado.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = metricasCliente.findIndex(m => m.id === active.id);
    const newIndex = metricasCliente.findIndex(m => m.id === over.id);
    const novaOrdem = arrayMove(metricasCliente, oldIndex, newIndex);
    setMetricasCliente(novaOrdem);
    for (let i = 0; i < novaOrdem.length; i++) {
      await supabase.from('ads_metricas_cliente').update({ ordem: i }).eq('id', novaOrdem[i].id);
    }
  };

  const metricaAtivaNoCliente = (metricaId) => metricasCliente.some(m => m.metrica_id === metricaId);
  const metricasPorGrupo = grupos.map(g => ({
    grupo: g,
    metricas: metricasGlobais.filter(m => m.grupo === g),
  })).filter(g => g.metricas.length > 0);

  const inputStyle = { backgroundColor: '#1a1a1a', border: '1px solid #333' };

  if (carregando) return <div className="flex items-center justify-center h-64"><p style={{ color: '#555' }}>Carregando...</p></div>;

  return (
    <div>
      <style>{`input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}`}</style>

      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setPaginaAtiva('ads')} className="text-sm" style={{ color: '#555' }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#555'}>
          ← Ads
        </button>
        <span style={{ color: '#333' }}>/</span>
        <h1 className="text-xl font-bold text-white">Configurações</h1>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Coluna esquerda — métricas globais */}
        <div>
          <div className="rounded-xl p-5 mb-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
            <h2 className="text-sm font-semibold text-white mb-1">Métricas predefinidas</h2>
            <p className="text-xs mb-4" style={{ color: '#555' }}>Lista global disponível para todos os clientes</p>
            {metricasPorGrupo.map(({ grupo, metricas }) => (
              <div key={grupo} className="mb-4">
                <p className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: '#444' }}>{grupo}</p>
                <div className="flex flex-col gap-2">
                  {metricas.map(m => (
                    <div key={m.id}>
                      {editandoMetrica?.id === m.id ? (
                        <div className="rounded-lg p-3 flex flex-col gap-2" style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                          <input type="text" value={editandoMetrica.nome}
                            onChange={(e) => setEditandoMetrica({ ...editandoMetrica, nome: e.target.value })}
                            className="w-full text-sm px-2 py-1.5 rounded outline-none text-white"
                            style={{ backgroundColor: '#222', border: '1px solid #444' }} />
                          <div className="flex gap-2">
                            <select value={editandoMetrica.tipo}
                              onChange={(e) => setEditandoMetrica({ ...editandoMetrica, tipo: e.target.value })}
                              className="flex-1 text-xs px-2 py-1.5 rounded outline-none text-white"
                              style={{ backgroundColor: '#222', border: '1px solid #444' }}>
                              {tipos.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                            </select>
                            <select value={editandoMetrica.grupo}
                              onChange={(e) => setEditandoMetrica({ ...editandoMetrica, grupo: e.target.value })}
                              className="flex-1 text-xs px-2 py-1.5 rounded outline-none text-white"
                              style={{ backgroundColor: '#222', border: '1px solid #444' }}>
                              {grupos.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => salvarEdicaoMetrica(m.id)}
                              className="flex-1 text-xs py-1.5 rounded font-medium"
                              style={{ backgroundColor: '#DCF127', color: '#0a0a0a' }}>Salvar</button>
                            <button onClick={() => setEditandoMetrica(null)}
                              className="flex-1 text-xs py-1.5 rounded"
                              style={{ backgroundColor: '#222', color: '#aaa' }}>Cancelar</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                          style={{ backgroundColor: '#1a1a1a', opacity: m.ativo ? 1 : 0.45 }}>
                          <span className="text-sm text-white flex-1">{m.nome}</span>
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: '#222', color: '#666' }}>{m.tipo}</span>
                          <button onClick={() => setEditandoMetrica({ id: m.id, nome: m.nome, tipo: m.tipo, grupo: m.grupo })}
                            className="text-xs transition-colors" style={{ color: '#444', background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#aaa'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#444'}>✎</button>
                          <button onClick={async () => {
                            if (!window.confirm(`Deletar "${m.nome}"?`)) return;
                            await supabase.from('ads_metricas_config').delete().eq('id', m.id);
                            await recarregarMetricasGlobais();
                            if (clienteSelecionado) carregarMetricasCliente(clienteSelecionado.id);
                          }} className="text-xs transition-colors" style={{ color: '#444', background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#444'}>✕</button>
                          <button onClick={() => toggleMetricaGlobal(m.id, m.ativo)}
                            style={{ width: '32px', height: '20px', borderRadius: '99px', backgroundColor: m.ativo ? '#DCF127' : '#333', border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'white', top: '4px', left: m.ativo ? '18px' : '3px', transition: 'left 0.15s' }} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
            <h2 className="text-sm font-semibold text-white mb-4">Nova métrica</h2>
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#555' }}>Nome</label>
                <input type="text" value={novaMetrica.nome}
                  onChange={(e) => setNovaMetrica({ ...novaMetrica, nome: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && adicionarMetrica()}
                  placeholder="Ex: CPL, ROI, Visualizações..."
                  className="w-full text-sm px-3 py-2 rounded-lg outline-none text-white"
                  style={inputStyle} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#555' }}>Tipo</label>
                <select value={novaMetrica.tipo}
                  onChange={(e) => setNovaMetrica({ ...novaMetrica, tipo: e.target.value })}
                  className="w-full text-sm px-3 py-2 rounded-lg outline-none text-white" style={inputStyle}>
                  {tipos.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: '#555' }}>Grupo</label>
                <select value={novaMetrica.grupo}
                  onChange={(e) => setNovaMetrica({ ...novaMetrica, grupo: e.target.value })}
                  className="w-full text-sm px-3 py-2 rounded-lg outline-none text-white" style={inputStyle}>
                  {grupos.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <button onClick={adicionarMetrica}
                className="w-full py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: '#DCF127', color: '#0a0a0a' }}>
                Adicionar métrica
              </button>
            </div>
          </div>
        </div>

        {/* Coluna direita — métricas por cliente */}
        <div>
          <div className="rounded-xl p-5" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
            <h2 className="text-sm font-semibold text-white mb-1">Métricas por cliente</h2>
            <p className="text-xs mb-4" style={{ color: '#555' }}>Ative, ordene arrastando e marque até 3 favoritas para o card.</p>

            <div className="flex gap-2 flex-wrap mb-4">
              {clientes.map(c => (
                <button key={c.id} onClick={() => setClienteSelecionado(c)}
                  className="text-xs px-3 py-1.5 rounded-full transition-all"
                  style={{
                    backgroundColor: clienteSelecionado?.id === c.id ? '#DCF127' : '#1a1a1a',
                    color: clienteSelecionado?.id === c.id ? '#0a0a0a' : '#666',
                    border: clienteSelecionado?.id === c.id ? 'none' : '1px solid #222',
                  }}>
                  {c.nome}
                </button>
              ))}
            </div>

            {clienteSelecionado && (
              <div>
                <p className="text-xs mb-3" style={{ color: '#444' }}>
                  {metricasCliente.length} ativas · {metricasCliente.filter(m => m.favorita).length}/3 favoritas · arraste ≡ para reordenar
                </p>

                {/* Métricas ativas — draggable */}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={metricasCliente.map(m => m.id)} strategy={verticalListSortingStrategy}>
                    {metricasCliente.map(mc => {
                      if (!mc.ads_metricas_config) return null;
                      const favoritasAtivas = metricasCliente.filter(x => x.favorita).length;
                      return (
                        <SortableMetricaItem
                          key={mc.id}
                          id={mc.id}
                          nome={mc.ads_metricas_config.nome}
                          grupo={mc.ads_metricas_config.grupo}
                          favorita={mc.favorita}
                          favoritasAtivas={favoritasAtivas}
                          onToggle={() => toggleMetricaCliente(mc.metrica_id)}
                          onToggleFavorita={() => toggleFavorita(mc.id, mc.favorita)}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>

                {/* Métricas disponíveis para ativar */}
                {metricasGlobais.filter(m => m.ativo && !metricaAtivaNoCliente(m.id)).length > 0 && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid #1a1a1a' }}>
                    <p className="text-xs mb-2" style={{ color: '#333' }}>Disponíveis para ativar</p>
                    {metricasGlobais.filter(m => m.ativo && !metricaAtivaNoCliente(m.id)).map(m => (
                      <div key={m.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg mb-1"
                        style={{ backgroundColor: '#111', opacity: 0.5 }}>
                        <div style={{ width: '14px', flexShrink: 0 }} />
                        <button onClick={() => toggleMetricaCliente(m.id)}
                          style={{ width: '32px', height: '20px', borderRadius: '99px', backgroundColor: '#333', border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'white', top: '4px', left: '3px' }} />
                        </button>
                        <span style={{ fontSize: '13px', color: 'white', flex: 1 }}>{m.nome}</span>
                        <span style={{ fontSize: '11px', color: '#444' }}>{m.grupo}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdsConfiguracoes;